import api from './api';
import { normalizeCompetitionOverview } from '../utils/normalizeCompetitionOverview';

/**
 * Competition overview content (the information page at
 * /student/competitions/:templateId).
 *
 * This is the ONLY place the overview data is read. It comes from the
 * competition's own guide: a super admin uploads the .docx/.pdf on the admin
 * Competition Setup page, the backend parses it with Claude
 * (competition_overview_extraction_service.py) and stores the structured result
 * on the template, and this endpoint serves it to enrolled students.
 *
 * Shape (every field except `templateId` and `published` may be null or absent;
 * the page hides a section that has no data and never fails on a missing one):
 *
 *   {
 *     templateId,                 // competition_templates.id
 *     published,                  // false -> no guide uploaded yet
 *     name,                       // null -> the page uses the real template name
 *     tagline, focus, participation, eligibility,
 *     officialWebsite: { label, url },
 *     currentStageKey,            // matches a stages[].key
 *     nextOfficialDeadline: { date: 'YYYY-MM-DD'|null, stageLabel, isPlaceholder },
 *     stages: [{
 *       key, number, name, isPlaceholderName,
 *       deadline: { date: 'YYYY-MM-DD'|null, isPlaceholder },
 *       description, includes: ['written', 'video', ...],
 *       deliverables: [{ title, requirement, note }]
 *     }],
 *     rules:   [{ label, text }],            // text null -> shown as "to be added"
 *     links:   [{ label, url }],
 *     process: [{ step, owner: 'you'|'mentor'|'portal', title, text }],
 *     roadmap: { now, afterReview, then }    // each { title, text }
 *   }
 *
 * `isPlaceholder: true` marks content that is still a stand-in; the page shows
 * a SAMPLE DATE / PLACEHOLDER tag next to it. Parsed overviews never set it —
 * their content is the document's or absent — but the flag stays in the
 * contract because the page still honours it.
 *
 * The result always goes through normalizeCompetitionOverview (utils), because
 * the stored data was parsed out of a document and cannot be trusted to be
 * complete or well-typed.
 */

/**
 * How applying works on CreatED, which is the same for every competition: the
 * student drafts, the mentor reviews, the official portal is the destination.
 * A constant rather than something the guide is parsed for — it describes our
 * review workflow, which no competition's rulebook knows about.
 */
const APPLICATION_PROCESS = [
  { step: 1, owner: 'you', title: 'Read the competition requirements', text: 'Understand the stages, deadlines, rules and required deliverables.' },
  { step: 2, owner: 'you', title: 'Prepare your first draft', text: 'Write the initial responses and create your application materials yourself.' },
  { step: 3, owner: 'you', title: 'Submit your drafts for review', text: 'Enter responses or upload files in your Cosmic workspace and submit each section.' },
  { step: 4, owner: 'mentor', title: 'Receive and incorporate feedback', text: 'Mentors review your work and may request revisions before approving sections.' },
  { step: 5, owner: 'you', title: 'Finalise your application', text: 'Once sections are approved, use the final content to complete your official submission.' },
  { step: 6, owner: 'portal', title: 'Submit on the official website', text: "The competition's official platform is the final submission destination." },
];

export const getCompetitionOverview = async (templateId) => {
  const response = await api.get(`/competition-workflow/templates/${templateId}/overview`);
  const data = response.data?.overview || null;

  return {
    ...normalizeCompetitionOverview(data, templateId),
    // What the page branches on for its "details coming soon" state. An upload
    // always produces at least one stage, rule or tagline (the backend refuses
    // a document it could read nothing from), so "has data" and "published"
    // are the same thing.
    published: Boolean(data),
    process: APPLICATION_PROCESS,
  };
};
